/**
 * DateRangeFilter — Shared date range filter component
 *
 * Reusable across admin pages (Lead Vault, Routing Inspector, Logs)
 * and future TJ Command Center dashboards (Media Buying, Financials).
 *
 * Features:
 * - Preset buttons: Today, 7d, 30d, 90d
 * - Custom date range via Calendar popover
 * - Clear/reset to "All time"
 * - Compact display with formatted date range label
 */

import { useState } from "react";
import { format, startOfDay, endOfDay, subDays } from "date-fns";
import { CalendarIcon, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import type { DateRange } from "react-day-picker";

export interface DateRangeValue {
  from: Date;
  to: Date;
}

export type PresetKey = "today" | "7d" | "30d" | "90d" | "custom";

interface DateRangeFilterProps {
  value: DateRangeValue | undefined;
  onChange: (range: DateRangeValue | undefined) => void;
  /** Currently active preset (managed externally for controlled behavior) */
  activePreset?: PresetKey | undefined;
  onPresetChange?: (preset: PresetKey | undefined) => void;
  className?: string;
}

const PRESETS: Array<{ key: PresetKey; label: string; days: number }> = [
  { key: "today", label: "Today", days: 0 },
  { key: "7d", label: "7 days", days: 7 },
  { key: "30d", label: "30 days", days: 30 },
  { key: "90d", label: "90 days", days: 90 },
];

function buildPresetRange(days: number): DateRangeValue {
  const now = new Date();
  return {
    from: days === 0 ? startOfDay(now) : startOfDay(subDays(now, days)),
    to: endOfDay(now),
  };
}

export function DateRangeFilter({
  value,
  onChange,
  activePreset,
  onPresetChange,
  className,
}: DateRangeFilterProps) {
  const [calendarOpen, setCalendarOpen] = useState(false);
  // Tracks partial selection (user picked "from" but not yet "to")
  const [pendingRange, setPendingRange] = useState<DateRange | undefined>(undefined);

  const handlePreset = (preset: typeof PRESETS[number]) => {
    const range = buildPresetRange(preset.days);
    onChange(range);
    onPresetChange?.(preset.key);
    setPendingRange(undefined);
  };

  const handleCustomSelect = (range: DateRange | undefined) => {
    if (range?.from && range?.to) {
      onChange({
        from: startOfDay(range.from),
        to: endOfDay(range.to),
      });
      onPresetChange?.("custom");
      setPendingRange(undefined);
      setCalendarOpen(false);
    } else {
      // Partial selection — store it so the calendar shows the "from" highlight
      setPendingRange(range);
    }
  };

  const handleOpenChange = (open: boolean) => {
    setCalendarOpen(open);
    if (!open) {
      // Reset partial selection when popover closes without completing
      setPendingRange(undefined);
    }
  };

  const handleClear = () => {
    onChange(undefined);
    onPresetChange?.(undefined);
    setPendingRange(undefined);
  };

  const displayLabel = value
    ? `${format(value.from, "MMM d")} – ${format(value.to, "MMM d, yyyy")}`
    : "All time";

  // Show pending partial selection while picking, otherwise the committed value
  const calendarSelected: DateRange | undefined = pendingRange
    ?? (value ? { from: value.from, to: value.to } : undefined);

  return (
    <div className={cn("flex items-center gap-1.5 flex-wrap", className)}>
      {PRESETS.map((preset) => (
        <Button
          key={preset.key}
          variant={activePreset === preset.key ? "default" : "outline"}
          size="sm"
          className="h-8 text-xs"
          onClick={() => handlePreset(preset)}
        >
          {preset.label}
        </Button>
      ))}

      <Popover open={calendarOpen} onOpenChange={handleOpenChange}>
        <PopoverTrigger asChild>
          <Button
            variant={activePreset === "custom" ? "default" : "outline"}
            size="sm"
            className="h-8 text-xs gap-1.5"
          >
            <CalendarIcon className="h-3.5 w-3.5" />
            {activePreset === "custom" ? displayLabel : "Custom"}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar
            mode="range"
            selected={calendarSelected}
            onSelect={handleCustomSelect}
            numberOfMonths={2}
            disabled={{ after: new Date() }}
            defaultMonth={value?.from ?? subDays(new Date(), 30)}
          />
        </PopoverContent>
      </Popover>

      {value && (
        <>
          <span className="text-xs text-muted-foreground ml-1">
            {displayLabel}
          </span>
          <Button
            variant="ghost"
            size="sm"
            className="h-6 w-6 p-0"
            onClick={handleClear}
            title="Clear date filter"
          >
            <X className="h-3.5 w-3.5" />
          </Button>
        </>
      )}
    </div>
  );
}
